import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { DocumentLineRow } from "@/features/document-lines/DocumentLineRow";
import {
  clearDocumentDraft,
  emptyDraftLine,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
} from "@/features/document-lines/documentDraft";
import { toMinor } from "@/features/document-lines/productDetails";
import { totalTaxableLines } from "@/lib/tax";
import { useOrganization } from "@/lib/organization";
import {
  acceptQuote,
  convertQuote,
  createQuote,
  createQuotePortalToken,
  declineQuote,
  getQuote,
  listChartAccounts,
  listQuotes,
  listTaxRates,
  portalQuoteUrl,
  type Quote,
  sendQuote,
  type TaxRate,
  updateQuote,
} from "@/lib/api";
import {
  quoteStatusLabel,
  toQuoteLinePayload,
  type QuoteLineDraft,
} from "./quoteForm";

const QUOTE_DRAFT_PREFIX = "niche-accounting.quote-draft.v1";

function errorText(cause: unknown) {
  return cause instanceof Error ? cause.message : String(cause);
}
function dateToday() {
  return new Date().toISOString().slice(0, 10);
}

export function QuoteDetailPage() {
  const { quoteId } = useParams({ strict: false }) as { quoteId?: string };
  const creating = !quoteId;
  const navigate = useNavigate();
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";
  const skipDraftSave = useRef(false);
  const draftExtras = useRef({ paymentTermId: "", crmContactId: "" });
  const [quote, setQuote] = useState<Quote | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [lines, setLines] = useState<QuoteLineDraft[]>([]);
  const [draftOrganizationId, setDraftOrganizationId] = useState<string | null>(null);
  const [knownCustomers, setKnownCustomers] = useState<string[]>([]);
  const [dueAt, setDueAt] = useState(dateToday());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [portalLink, setPortalLink] = useState<string | null>(null);
  const [accountLabels, setAccountLabels] = useState<Map<string, string>>(
    new Map(),
  );
  const [confirming, setConfirming] = useState<
    "send" | "accept" | "decline" | null
  >(null);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);

  // Unsaved quotes stay in this browser session until the first save creates one.
  useEffect(() => {
    if (!creating) {
      setDraftOrganizationId(null);
      return;
    }
    skipDraftSave.current = false;
    const loaded =
      loadDocumentDraft(QUOTE_DRAFT_PREFIX, organizationId) ?? newDocumentDraft();
    draftExtras.current = {
      paymentTermId: loaded.paymentTermId,
      crmContactId: loaded.crmContactId,
    };
    setCustomerName(loaded.partyName);
    setIssueDate(loaded.issueDate);
    setExpiryDate(loaded.dueAt);
    setLines(loaded.lines);
    setQuote(null);
    setDraftOrganizationId(organizationId);
  }, [creating, organizationId]);

  useEffect(() => {
    if (!creating || draftOrganizationId !== organizationId || skipDraftSave.current) {
      return;
    }
    saveDocumentDraft(QUOTE_DRAFT_PREFIX, organizationId, {
      partyName: customerName,
      issueDate,
      dueAt: expiryDate,
      paymentTermId: draftExtras.current.paymentTermId,
      crmContactId: draftExtras.current.crmContactId,
      lines,
    });
  }, [
    creating,
    draftOrganizationId,
    organizationId,
    customerName,
    issueDate,
    expiryDate,
    lines,
  ]);

  const load = useCallback(() => {
    if (!quoteId) return;
    getQuote(quoteId)
      .then((loaded) => {
        setQuote(loaded);
        setCustomerName(loaded.customer_name);
        setIssueDate(loaded.issue_date);
        setExpiryDate(loaded.expiry_date);
        setLines(
          (loaded.lines ?? []).map((line) => ({
            uid: line.id,
            account_id: line.account_id,
            description: line.description,
            amount: (line.amount_minor / 100).toFixed(2),
            tax_rate_id: line.tax_rate_id ?? "",
            sku_id: line.sku_id ?? "",
            quantity:
              line.quantity_amount != null
                ? String(
                    line.quantity_amount / 10 ** (line.quantity_scale ?? 0),
                  )
                : "",
            unit:
              typeof line.unit === "string"
                ? line.unit
                : (line.unit?.custom ?? ""),
            unit_price:
              line.unit_price_cents != null
                ? (line.unit_price_cents / 100).toFixed(2)
                : "",
          })),
        );
      })
      .catch((cause) => setError(errorText(cause)));
    listChartAccounts()
      .then((accounts) =>
        setAccountLabels(
          new Map(
            accounts.map((account) => [
              account.id,
              `${account.code} — ${account.name}`,
            ]),
          ),
        ),
      )
      .catch(() => setAccountLabels(new Map()));
  }, [quoteId]);
  useEffect(load, [load]);
  useEffect(() => {
    let cancelled = false;
    listTaxRates()
      .then((rows) => {
        if (!cancelled) setTaxRates(rows);
      })
      .catch(() => {
        if (!cancelled) setTaxRates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const editingDraft = creating || quote?.status === "draft";
  useEffect(() => {
    if (!editingDraft) return;
    let cancelled = false;
    listQuotes()
      .then((rows) => {
        if (cancelled) return;
        const names = new Set<string>();
        for (const row of rows) {
          const name = row.customer_name.trim();
          if (name) names.add(name);
        }
        setKnownCustomers([...names].sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        if (!cancelled) setKnownCustomers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [editingDraft]);

  const taxRateBpById = new Map(
    taxRates.map((rate) => [rate.id, rate.rate_bp]),
  );
  const totals = totalTaxableLines(
    lines.map((line) => ({
      amountCents: toMinor(line.amount) ?? 0,
      rateBp: line.tax_rate_id
        ? taxRateBpById.get(line.tax_rate_id)
        : undefined,
    })),
  );
  const currency = quote?.currency_code ?? "USD";
  const action = async (name: string, run: () => Promise<unknown>) => {
    setBusy(name);
    setError(null);
    try {
      await run();
      load();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(null);
    }
  };
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await action("save", async () => {
      if (!customerName.trim()) throw new Error("Customer name is required.");
      if (!issueDate || !expiryDate || expiryDate < issueDate)
        throw new Error("Expiry date must be on or after the issue date.");
      const input = {
        customer_name: customerName.trim(),
        issue_date: issueDate,
        expiry_date: expiryDate,
        lines: toQuoteLinePayload(lines),
      };
      if (!quoteId) {
        const created = await createQuote(input);
        skipDraftSave.current = true;
        clearDocumentDraft(QUOTE_DRAFT_PREFIX, organizationId);
        await navigate({
          to: "/quotes/$quoteId",
          params: { quoteId: created.id },
        });
        return;
      }
      await updateQuote(quoteId, input);
    });
  };
  const copyPortal = async () => {
    if (!quoteId) return;
    setBusy("portal");
    try {
      const minted = await createQuotePortalToken(quoteId);
      const url = portalQuoteUrl(minted.token);
      try {
        await navigator.clipboard.writeText(url);
        setPortalLink(`Customer link copied: ${url}`);
      } catch {
        setPortalLink(`Customer link (copy manually): ${url}`);
      }
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(null);
    }
  };
  const convert = async () => {
    if (!quoteId) return;
    await action("convert", async () => {
      if (!dueAt) throw new Error("Invoice due date is required.");
      const result = await convertQuote(quoteId, {
        due_at: new Date(`${dueAt}T00:00:00`).toISOString(),
      });
      await navigate({
        to: "/invoices/$invoiceId",
        params: { invoiceId: result.invoice_id },
      });
    });
  };
  const updateLine = (index: number, patch: Partial<QuoteLineDraft>) =>
    setLines((all) =>
      all.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );

  const draftForm = (
    <form onSubmit={save} className="surface grid gap-4 rounded-2xl p-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <label
          htmlFor="quote-customer-name"
          className="grid gap-1 text-xs font-medium"
        >
          Customer name
          <Input
            id="quote-customer-name"
            list="quote-customer-options"
            value={customerName}
            onChange={(event) => setCustomerName(event.target.value)}
            placeholder="Search or type a name"
            autoComplete="off"
            required
          />
          <datalist id="quote-customer-options">
            {knownCustomers.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </label>
        <label
          htmlFor="quote-issue-date"
          className="grid gap-1 text-xs font-medium"
        >
          Issue date
          <Input
            id="quote-issue-date"
            type="date"
            value={issueDate}
            onChange={(event) => setIssueDate(event.target.value)}
            required
          />
        </label>
        <label
          htmlFor="quote-expiry-date"
          className="grid gap-1 text-xs font-medium"
        >
          Expires
          <Input
            id="quote-expiry-date"
            type="date"
            value={expiryDate}
            onChange={(event) => setExpiryDate(event.target.value)}
            required
          />
        </label>
      </div>
      {lines.map((line, index) => (
        <DocumentLineRow
          key={line.uid}
          idPrefix={`quote-line-${line.uid}`}
          index={index}
          line={line}
          onChange={(patch) => updateLine(index, patch)}
          onRemove={() =>
            setLines((all) => all.filter((_, i) => i !== index))
          }
          canRemove={lines.length > 1}
        />
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setLines((all) => [...all, emptyDraftLine()])}
        >
          Add line
        </Button>
        <Button type="submit" disabled={busy !== null}>
          {busy === "save" ? "Saving…" : "Save draft"}
        </Button>
        <span
          className="text-sm text-[var(--muted-foreground)]"
          aria-live="polite"
          data-testid="quote-detail-totals"
        >
          Subtotal:{" "}
          <span className="font-semibold text-[var(--foreground)]">
            {formatMoney({
              cents: totals.subtotalCents,
              currency,
            })}
          </span>{" "}
          — Tax (estimated):{" "}
          <span className="font-semibold text-[var(--foreground)]">
            {formatMoney({
              cents: totals.taxCents,
              currency,
            })}
          </span>{" "}
          — Total:{" "}
          <span className="font-semibold text-[var(--foreground)]">
            {formatMoney({
              cents: totals.totalCents,
              currency,
            })}
          </span>
        </span>
      </div>
    </form>
  );

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/quotes"
          className="text-[var(--muted-foreground)] hover:underline"
        >
          Quotes
        </Link>
        <span className="mx-2">/</span>
        <span>{creating ? "New quote" : (quote?.quote_number ?? quoteId)}</span>
      </nav>
      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          {quoteId ? (
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={load}
              >
                Retry
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {portalLink ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm break-all text-[var(--success)]">
          {portalLink}
        </output>
      ) : null}
      {creating ? (
        draftOrganizationId === organizationId ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  New quote
                </h1>
                <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                  {customerName.trim()
                    ? `${customerName.trim()} · Draft`
                    : "Draft"}
                </p>
              </div>
            </div>
            {draftForm}
          </>
        ) : (
          <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
        )
      ) : !quote ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {quote.quote_number}
              </h1>
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                {quote.customer_name} · {quoteStatusLabel(quote.status)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {quote.status === "draft" ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirming("send")}
                  disabled={busy !== null}
                >
                  {busy === "send" ? "Saving…" : "Mark as sent"}
                </Button>
              ) : null}
              {quote.status === "sent" ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => setConfirming("accept")}
                    disabled={busy !== null}
                    title="Use this when the customer accepted the quote outside Accounting"
                  >
                    {busy === "accept" ? "Saving…" : "Mark accepted"}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setConfirming("decline")}
                    disabled={busy !== null}
                  >
                    {busy === "decline" ? "Saving…" : "Decline"}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={copyPortal}
                    disabled={busy !== null}
                  >
                    {busy === "portal" ? "Copying…" : "Copy customer link"}
                  </Button>
                </>
              ) : null}
            </div>
          </div>
          {quote.status === "converted" && quote.converted_invoice_id ? (
            <div className="surface rounded-xl p-4 text-sm">
              Converted to{" "}
              <Link
                to="/invoices/$invoiceId"
                params={{ invoiceId: quote.converted_invoice_id }}
                className="text-[var(--action)] hover:underline"
              >
                invoice
              </Link>
              .
            </div>
          ) : null}
          {quote.status === "accepted" ? (
            <div className="surface flex flex-wrap items-end gap-3 rounded-xl p-4">
              <label
                htmlFor="quote-due-at"
                className="grid gap-1 text-xs font-medium"
              >
                New invoice due date
                <Input
                  id="quote-due-at"
                  type="date"
                  value={dueAt}
                  onChange={(event) => setDueAt(event.target.value)}
                  required
                />
              </label>
              <Button onClick={convert} disabled={busy !== null}>
                {busy === "convert" ? "Converting…" : "Convert to invoice"}
              </Button>
            </div>
          ) : null}
          {quote.status === "draft" ? (
            draftForm
          ) : (
            <div className="surface overflow-hidden rounded-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(quote.lines ?? []).map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.description}</TableCell>
                      <TableCell className="text-xs">
                        {accountLabels.get(line.account_id) ?? (
                          <span
                            className="text-[var(--muted-foreground)]"
                            title={line.account_id}
                          >
                            Unknown account
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {line.tax_cents > 0 ? (
                          formatMoney({
                            cents: line.tax_cents,
                            currency: quote.currency_code ?? "USD",
                          })
                        ) : (
                          <span className="text-[var(--muted-foreground)]">
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: line.amount_minor,
                          currency: quote.currency_code ?? "USD",
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={
          confirming === "send"
            ? "Mark this quote as sent?"
            : confirming === "accept"
              ? "Mark this quote accepted?"
              : "Decline this quote?"
        }
        description={
          confirming === "send"
            ? "This changes the quote status so it can be accepted. It does not email the customer — copy the customer link to share it."
            : confirming === "accept"
              ? "Use this when the customer accepted the quote outside Accounting. The quote can then be converted to a draft invoice."
              : "The quote will be marked declined and can no longer be accepted or converted."
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const actionName = confirming;
              setConfirming(null);
              if (!quoteId || !actionName) return;
              if (actionName === "send")
                void action("send", () => sendQuote(quoteId));
              if (actionName === "accept")
                void action("accept", () => acceptQuote(quoteId));
              if (actionName === "decline")
                void action("decline", () => declineQuote(quoteId));
            }}
          >
            {confirming === "send"
              ? "Mark as sent"
              : confirming === "accept"
                ? "Mark accepted"
                : "Decline quote"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
